/**
 * **债权债务** —— 「家底」组第 2 项（批次 `94` 首次落地）
 *
 * 依据：`DR-025`（导航三组 10 项，`debt` 一直是**占位无页面**）·
 * 原型 `docs/03-UX与UI设计/15-债权债务_原型.html`（本页三段：借贷时间线 / 信用卡账单 / 往来账户）·
 * `DR-022`（往来账户化）· `AC-Q4.1~Q4.6` · `DR-044` / `DR-049` / `DR-053`。
 *
 * ## 🔴 这一页为什么现在才有，以及它解决什么
 *
 * 之前「债权债务」在导航里**点了没反应**（`nav.tsx` 的 `debt` 没有 `path`），
 * 所有往来卡片 + 12 张信用卡都挤在「资产」页的账户列表里。
 * 负责人 2026-10-21：「信用卡在资产里面不太方便查看」「把借贷、预付、待摊放到债权债务模块中，
 * 看起来更清楚」。
 *
 * 本页承载四类东西，**按"欠 / 被欠 / 待摊"三段**排：
 *
 * | 段 | 内容 | 数据来源 |
 * |---|---|---|
 * | ① 信用卡账单 | 本期应还 · 账期 · 剩余额度 · 未出 · 往期 3 期 | `credit` 账户 |
 * | ② 债权债务账户 | 借贷 / 应收 / 应付 / 垫付 / 报销（按 `kind` 分组 + 折叠） | `contra` 账户 |
 * | ③ 预付与待摊 | 内部挂账里"钱已经付出去但还没变成支出"的两类 | `internal` 的 `prepaid` / `amortize` |
 *
 * ## 🔴 账户的增删改**跟着展示走**
 *
 * 资产页只管「日常 / 社保」两类账户；本页管它展示的四类。
 * 两条页面各自持有自己的 `AccountForm` 接线（草稿与请求体的构造**不重复** ——
 * 唯一实现在 `features/account-draft.ts`），避免"在一个页面能改、在另一个页面改不了"。
 *
 * ## ⚠️ 本批不做（诚实登记）
 *
 * - **预付 / 待摊的分摊进度**（还剩几期待摊）：需要按 `schedules` 聚合，
 *   属 F 域能力，本页只给余额（不臆造进度条）。
 * - **借贷时间线卡**（原型左上那张"房贷 · 资金流水时间线"）：贷款卡在「借贷」分组里
 *   已有「明细」抽屉（`AC-Q4.3`），时间线视图与它重复，**本页仍不单列**。
 *   🆕 批 `111`：明细另有一条**可直达的页面** `/debts/<contraId>`（内容体与抽屉共用
 *   `ui/ContraTimelineBody.tsx`）—— 深链接走页面，本页的批量浏览仍走抽屉，
 *   两处渲染只有一份实现。
 */

import { useRef, useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountType,
  InternalCategory,
  centsToYuanText,
  toCents,
  type AccountView,
  type Cents,
  type CreateAccountRequest,
} from '@jiaying/shared';
import { createAccount, deleteAccount, listAccounts, updateAccount } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { Amount } from '../ui/Amount.js';
import { AnchorBar } from '../ui/AnchorBar.js';
import { AccountForm } from '../ui/AccountForm.js';
import { ContraSection } from '../ui/ContraSection.js';
import { Loading } from '../ui/Loading.js';
import { apiMessages, deleteAdvice } from '../features/account-errors.js';
import {
  buildCreateAccountRequest,
  buildUpdateAccountRequest,
  draftFromView,
  emptyAccountDraft,
  type AccountDraft,
} from '../features/account-draft.js';

export function DebtsPage(): ReactElement {
  const qc = useQueryClient();
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const [editing, setEditing] = useState<'create' | string | null>(null);
  const [draft, setDraft] = useState<AccountDraft>(() => emptyAccountDraft());
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [balanceWarning, setBalanceWarning] = useState<{ id: string; message: string } | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const close = (): void => {
    setEditing(null);
    setErrors([]);
    setConfirmingId(null);
    setBalanceWarning(null);
  };

  const save = useMutation({
    mutationFn: (req: CreateAccountRequest): Promise<AccountView> =>
      editing === null || editing === 'create' ? createAccount(req) : updateAccount(editing, req),
    onSuccess: () => {
      close();
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      void qc.invalidateQueries({ queryKey: ['creditBills'] });
    },
    onError: (e: unknown) => {
      setErrors(apiMessages(e));
    },
  });

  const remove = useMutation({
    mutationFn: ({ id, confirmed }: { id: string; confirmed: boolean }) => deleteAccount(id, confirmed),
    onSuccess: () => {
      close();
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      void qc.invalidateQueries({ queryKey: ['creditBills'] });
    },
    onError: (e: unknown, vars: { id: string; confirmed: boolean }) => {
      const details =
        e instanceof ApiError ? (e.body.details as { requiresConfirmation?: unknown } | undefined) : undefined;
      if (details?.requiresConfirmation === true) {
        setConfirmingId(null);
        // 余额非零 ⇒ 升级成"我知道会清零"的确认态（文案用服务端原文，端侧不重算金额）
        setBalanceWarning({
          id: vars.id,
          message: e instanceof ApiError ? e.message : String(e),
        });
        return;
      }
      const advice = deleteAdvice(e);
      setErrors(advice === null ? apiMessages(e) : [...apiMessages(e), advice]);
    },
  });

  if (accounts.isPending) return <Loading />;
  if (accounts.isError) return <Alert severity="error">账户读取失败：{String(accounts.error)}</Alert>;

  const all = accounts.data.items;
  const busy = save.isPending || remove.isPending;

  /** 预付 / 待摊（`internal` 的两个品类）—— 原型把它们与往来放在同一模块里 */
  const deferred = all.filter(
    (a) =>
      a.type === AccountType.Internal &&
      (a.category === InternalCategory.Prepaid || a.category === InternalCategory.Amortize),
  );

  const openCreate = (category: InternalCategory | null): void => {
    setDraft(
      category === null
        ? { ...emptyAccountDraft(), type: AccountType.Contra }
        : { ...emptyAccountDraft(), type: AccountType.Internal, category },
    );
    setErrors([]);
    setConfirmingId(null);
    setBalanceWarning(null);
    setEditing('create');
  };

  const openEdit = (a: AccountView): void => {
    setDraft(draftFromView(a));
    setErrors([]);
    setConfirmingId(null);
    setBalanceWarning(null);
    setEditing(a.id);
  };

  const submit = (): void => {
    const built =
      editing === 'create' ? buildCreateAccountRequest(draft) : buildUpdateAccountRequest(draft);
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors([]);
    save.mutate(built.request as CreateAccountRequest);
  };

  const editingView =
    editing !== null && editing !== 'create' ? all.find((a) => a.id === editing) : undefined;

  return (
    <Stack spacing={2} ref={contentRef}>
      {/* 吸顶页内导航（`AC-O2.36`）—— 必须是内容区第一个子节点 */}
      <AnchorBar contentRef={contentRef} />

      {/* 「余额会清零」的服务端确认 —— 独立于表单（此刻表单已关闭） */}
      {balanceWarning !== null && (
        <Alert
          severity="warning"
          data-testid="account-balance-warning"
          action={
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                color="error"
                variant="contained"
                disabled={busy}
                onClick={() => {
                  remove.mutate({ id: balanceWarning.id, confirmed: true });
                }}
                data-testid="account-balance-confirm"
              >
                确认清零并删除
              </Button>
              <Button size="small" onClick={close} disabled={busy}>
                取消
              </Button>
            </Stack>
          }
        >
          {balanceWarning.message}
        </Alert>
      )}

      {/*
        🔴 批次 97（`DR-057` D2）：**信用卡账单区已整体搬到「账户」页的信用卡分组**。
        负责人 2026-10-23 改判「全部在账户内操作」——本页不再重复渲染，
        也不再留入口卡（账户页就在导航的同一组里，多一层入口只是多一次点击）。
      */}

      {/* ① 债权债务账户（contra 五类 · 分组即折叠 · 结算 / 报销 / 还本期 / 明细） */}
      <ContraSection
        accounts={all}
        onCreate={() => {
          openCreate(null);
        }}
        onEdit={openEdit}
        onDelete={(a) => {
          remove.mutate({ id: a.id, confirmed: false });
        }}
        busy={busy}
      />

      {/* ③ 预付 / 待摊 —— 钱已付出但还没变成支出（`internal` 两个品类） */}
      <Card data-testid="deferred-section" data-anchor="预付与待摊">
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                预付与待摊（{deferred.length}）
              </Typography>
              <Typography variant="caption" color="text.secondary">
                钱已经付出去、但还没到"变成支出"的时候 —— 预付卡余额 / 待摊费用余额
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  openCreate(InternalCategory.Prepaid);
                }}
                disabled={busy}
                data-testid="new-prepaid"
              >
                新建预付
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  openCreate(InternalCategory.Amortize);
                }}
                disabled={busy}
                data-testid="new-amortize"
              >
                新建待摊
              </Button>
            </Stack>
          </Stack>
        </CardContent>
        <Divider />
        {deferred.length === 0 ? (
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              还没有预付 / 待摊账户。常见的两种：
              <b>预付</b> 是充进商家卡里还没花完的钱（如洗车卡、餐卡）；
              <b>待摊</b> 是一次付掉、按月摊进支出的费用（如年费、保险）。
            </Typography>
          </CardContent>
        ) : (
          <List dense>
            {deferred.map((a) => (
              <ListItem key={a.id} divider disablePadding sx={{ px: 2, py: 1 }}>
                <ListItemText
                  primary={a.name}
                  secondary={`${a.ownerName} · ${a.currency}${a.categoryLabel === null ? '' : ` · ${a.categoryLabel}`}${
                    a.hidden ? ' · 已收起' : ''
                  }`}
                  slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
                />
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
                  <Amount
                    cents={toCents(a.balance)}
                    currency={a.currency}
                    colored={false}
                    sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      openEdit(a);
                    }}
                    disabled={busy}
                    data-testid={`deferred-edit-${a.id}`}
                  >
                    编辑
                  </Button>
                  {confirmingId === a.id ? (
                    <>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        disabled={busy}
                        onClick={() => {
                          setConfirmingId(null);
                          remove.mutate({ id: a.id, confirmed: false });
                        }}
                        data-testid={`deferred-delete-confirm-${a.id}`}
                      >
                        确认删除
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setConfirmingId(null);
                        }}
                        disabled={busy}
                      >
                        取消
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      color="error"
                      disabled={busy}
                      onClick={() => {
                        setConfirmingId(a.id);
                      }}
                      data-testid={`deferred-delete-${a.id}`}
                    >
                      删除
                    </Button>
                  )}
                </Stack>
              </ListItem>
            ))}
          </List>
        )}
      </Card>

      {/* 账户表单（内联，不弹 Dialog）—— 承载往来与预付/待摊两类 */}
      {editing !== null ? (
        <AccountForm
          draft={draft}
          onChange={(patch) => {
            setDraft((d) => ({ ...d, ...patch }));
          }}
          errors={errors}
          busy={busy}
          mode={editing === 'create' ? 'create' : 'edit'}
          readonlyView={
            editingView === undefined
              ? undefined
              : {
                  ownerName: editingView.ownerName,
                  // 初始余额走内核定点格式化（恒两位小数、不带币种符 —— 标签里已有币种）
                  initialBalanceText: centsToYuanText(toCents(editingView.initialBalance as Cents)),
                }
          }
          onSubmit={submit}
          onCancel={close}
        />
      ) : (
        errors.length > 0 && (
          <Alert severity="error" data-testid="debts-page-errors">
            <Stack component="ul" sx={{ m: 0, pl: 2 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </Stack>
          </Alert>
        )
      )}
    </Stack>
  );
}
